import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Project Requests — admin list of Large Projects client project
// requests, newest first, with a status filter.
//
// Consumes besthand-api `adminListProjectRequests(status, limit, offset)` (NEW
// admin query; returns `projectRequests`) through the `/lp-graphql` proxy via
// `lpApi`. Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_REQUESTS = `
  query ($status: String, $limit: Int, $offset: Int) {
    adminListProjectRequests(status: $status, limit: $limit, offset: $offset) {
      status errorMessage
      projectRequests {
        id clientUserId category subcategory title status
        siteCity siteState siteZip budgetMin budgetMax budgetIsFirm
        completenessScore visitFeePaid publishedAt createdAt
      }
    }
  }
`;

// ADMIN action — cancel a request (blocked server-side if it carries a live
// funded contract; admin-gated via requireAdmin + gateLargeProjects).
const M_CANCEL_REQUEST = `
  mutation ($projectRequestId: String!, $reason: String) {
    adminCancelProjectRequest(projectRequestId: $projectRequestId, reason: $reason) {
      status errorMessage
      projectRequest { id status }
    }
  }
`;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'MATCHED', label: 'Matched' },
  { value: 'CONTRACTED', label: 'Contracted' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  select: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', background: '#fff', cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  catTag: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: '#eef2f4', color: '#5a6168', marginLeft: 8 },
  badge: (kind) => {
    const map = {
      PUBLISHED: { bg: '#e7f6ec', fg: '#137a37' },
      MATCHED: { bg: '#e7f6ec', fg: '#137a37' },
      CONTRACTED: { bg: '#e7f6ec', fg: '#137a37' },
      DRAFT: { bg: '#eef2ff', fg: '#3730a3' },
      CLOSED: { bg: '#eef2f4', fg: '#5a6168' },
      CANCELLED: { bg: '#fde7e8', fg: '#c2151c' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtMoney = (v) => (v == null ? null : `$${Number(v).toLocaleString()}`);
const fmtBudget = (r) => {
  const lo = fmtMoney(r.budgetMin); const hi = fmtMoney(r.budgetMax);
  if (lo && hi) return `${lo}–${hi}${r.budgetIsFirm ? ' (firm)' : ''}`;
  return lo || hi || null;
};
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function ProjectRequests() {
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState('');

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (statusArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_REQUESTS, { status: statusArg || null, limit: 200, offset: 0 });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.adminListProjectRequests;
        if (env && env.status === 200) setRequests(env.projectRequests || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load project requests: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(status); }, [load, status]);

  const cancelRequest = (r) => {
    const ok = typeof window !== 'undefined' && window.confirm(
      `CANCEL project request #${r.id}${r.title ? ` — "${r.title}"` : ''}?\n\n`
      + 'This sets the request to CANCELLED and voids any open quotes. '
      + 'It is BLOCKED if the request has a live (funded) contract — cancel the contract first.',
    );
    if (!ok) return;
    const reason = window.prompt(`Reason for cancelling request #${r.id} (optional):`);
    if (reason === null) return;
    setBusyId(`c${r.id}`);
    setMsg(null);
    lpApi(M_CANCEL_REQUEST, { projectRequestId: String(r.id), reason: reason.trim() || null })
      .then(async (res) => {
        if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
        const env = res.data && res.data.adminCancelProjectRequest;
        if (env && env.status >= 200 && env.status < 300) { setMsg({ ok: `Request #${r.id} cancelled.` }); await load(status); }
        else { setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` }); }
        setBusyId('');
      })
      .catch((e) => { setMsg({ error: `Action failed: ${e.message}` }); setBusyId(''); });
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Project Requests</h2>
      <div style={S.hint}>
        Large Projects — client project requests, newest first. Filter by status.
      </div>

      <div style={S.toolbar}>
        <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={S.btnGhost} onClick={() => load(status)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{requests.length} shown</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        requests.length === 0 ? (
          <div style={S.empty}>No project requests for this filter.</div>
        ) : requests.map((r) => (
          <div style={S.card} key={r.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>
                  {r.title || `Request #${r.id}`}
                  {r.category ? <span style={S.catTag}>{r.category}{r.subcategory ? ` · ${r.subcategory}` : ''}</span> : null}
                </p>
                <div style={S.meta}>
                  #{r.id} · Client {r.clientUserId}
                  {[r.siteCity, r.siteState].filter(Boolean).length ? ` · ${[r.siteCity, r.siteState].filter(Boolean).join(', ')}` : ''}
                  {r.siteZip ? ` ${r.siteZip}` : ''}
                  {fmtBudget(r) ? ` · Budget ${fmtBudget(r)}` : ''}
                </div>
                <div style={S.meta}>
                  {r.completenessScore != null ? `Completeness ${r.completenessScore}% · ` : ''}
                  {r.visitFeePaid ? 'Visit fee paid · ' : ''}
                  {r.publishedAt ? `Published ${fmtDate(r.publishedAt)} · ` : ''}
                  Created {r.createdAt ? fmtDate(r.createdAt) : '—'}
                </div>
              </div>
              <span style={S.badge(r.status)}>{r.status || '—'}</span>
            </div>
            {r.status !== 'CANCELLED' && r.status !== 'COMPLETED' && (
              <div style={S.actions}>
                <button style={S.btnRed} disabled={busyId === `c${r.id}`} onClick={() => cancelRequest(r)}>
                  {busyId === `c${r.id}` ? '…' : 'Cancel request'}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default ProjectRequests;
